import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, orderBy, query } from 'firebase/firestore';
import { EmptyState } from '../components/EmptyState';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field, TextArea, TextInput } from '../components/ui/Field';
import { FormPanel, PageHeader, RecordGroup, SearchField } from '../components/ui/Page';
import { Switch } from '../components/ui/Switch';
import { ReadFailure } from '../components/ReadFailure';
import { listenQuery } from '../lib/liveRead';
import { useAuth } from '../auth/AuthProvider';
import { db, functions } from '../lib/firebase';
import { formatTimestamp } from '../lib/format';
import { canManageCustomers, type Customer } from '../types/models';

type CustomerForm = {
  name: string;
  nickname: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyStatus: string;
  notes: string;
  isActive: boolean;
};

const EMPTY_FORM: CustomerForm = {
  name: '',
  nickname: '',
  companyName: '',
  companyAddress: '',
  companyPhone: '',
  companyStatus: '',
  notes: '',
  isActive: true,
};

export default function CustomersPage() {
  const { activeCompanyId, activeRole } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);

  const canManage = canManageCustomers(activeRole ?? undefined);

  useEffect(() => {
    if (!activeCompanyId) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setCustomers([]);
    setError(null);
    setReadError(null);
    const customersRef = collection(db, 'companies', activeCompanyId, 'customers');
    const q = query(customersRef, orderBy('name'));
    const unsub = listenQuery(
      q,
      (snap) => {
        setCustomers(
          snap.docs.map((customerDoc) => ({
            id: customerDoc.id,
            ...(customerDoc.data() as Omit<Customer, 'id'>),
          }))
        );
        setReadError(null);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setReadError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [activeCompanyId, readAttempt]);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) =>
      [
        customer.name,
        customer.nickname,
        customer.companyName,
        customer.companyAddress,
        customer.companyPhone,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [customers, search]);

  const activeCustomers = filteredCustomers.filter(
    (customer) => customer.isActive !== false
  );
  const archivedCustomers = filteredCustomers.filter(
    (customer) => customer.isActive === false
  );

  function startNew() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNotice(null);
  }

  function startEdit(customer: Customer) {
    setEditing(customer);
    setForm({
      name: customer.name ?? '',
      nickname: customer.nickname ?? '',
      companyName: customer.companyName ?? '',
      companyAddress: customer.companyAddress ?? '',
      companyPhone: customer.companyPhone ?? '',
      companyStatus: customer.companyStatus ?? '',
      notes: customer.notes ?? '',
      isActive: customer.isActive !== false,
    });
    setNotice(null);
  }

  function setField<K extends keyof CustomerForm>(
    key: K,
    value: CustomerForm[K]
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeCompanyId || !canManage || !form.name.trim()) return;

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveCustomer = httpsCallable<
        {
          companyId: string;
          customerId?: string;
          name: string;
          nickname?: string;
          companyName?: string;
          companyAddress?: string;
          companyPhone?: string;
          companyStatus?: string;
          notes?: string;
          isActive: boolean;
        },
        { success: boolean; customerId: string }
      >(functions, 'saveCustomer');

      await saveCustomer({
        companyId: activeCompanyId,
        customerId: editing?.id,
        name: form.name.trim(),
        nickname: form.nickname.trim(),
        companyName: form.companyName.trim(),
        companyAddress: form.companyAddress.trim(),
        companyPhone: form.companyPhone.trim(),
        companyStatus: form.companyStatus.trim(),
        notes: form.notes.trim(),
        isActive: form.isActive,
      });
      setNotice(editing ? 'Customer updated.' : 'Customer added.');
      setEditing(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not save customer.');
    } finally {
      setSaving(false);
    }
  }

  async function archive(customer: Customer, isActive: boolean) {
    if (!activeCompanyId || !canManage || !customer.name) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveCustomer = httpsCallable<
        {
          companyId: string;
          customerId: string;
          name: string;
          nickname?: string;
          companyName?: string;
          companyAddress?: string;
          companyPhone?: string;
          companyStatus?: string;
          notes?: string;
          isActive: boolean;
        },
        { success: boolean; customerId: string }
      >(functions, 'saveCustomer');
      await saveCustomer({
        companyId: activeCompanyId,
        customerId: customer.id,
        name: customer.name,
        nickname: customer.nickname ?? '',
        companyName: customer.companyName ?? '',
        companyAddress: customer.companyAddress ?? '',
        companyPhone: customer.companyPhone ?? '',
        companyStatus: customer.companyStatus ?? '',
        notes: customer.notes ?? '',
        isActive,
      });
      setNotice(isActive ? 'Customer reactivated.' : 'Customer archived.');
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not update customer.');
    } finally {
      setSaving(false);
    }
  }

  if (!canManage) {
    return (
      <EmptyState
        title="Your role can't manage customers"
        description="Admins, dispatchers and managers keep the company's customer list, as in the iOS app."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Customers"
        description="The list every job picker uses, on web and on the phone."
        action={<Button type="button" onClick={startNew}>Add customer</Button>}
      />

      <div className="mt-5 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div>
          <SearchField
            label="Search customers"
            placeholder="Search name, address or phone"
            value={search}
            onChange={setSearch}
          />

          <div className="mt-3 space-y-2.5 empty:hidden">
            {error && <Alert tone="error">{error}</Alert>}
            {notice && <Alert tone="success">{notice}</Alert>}
            {readError && <ReadFailure message={readError} retry={() => setReadAttempt((n) => n + 1)} />}
          </div>

          {loading ? (
            <div className="mt-3 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-[14px] border border-hair bg-elev px-4 py-3.5">
                  <div className="skeleton h-4 w-40 rounded-md" />
                  <div className="skeleton mt-2 h-3.5 w-56 rounded-md" />
                </div>
              ))}
            </div>
          ) : readError ? null : customers.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                title="No customers yet"
                description="Add customers here so web and iOS job pickers use the same list."
              />
            </div>
          ) : (
            <>
              <RecordGroup title="Active" count={activeCustomers.length} emptyLabel="No active customers match that search.">
                {activeCustomers.map((customer) => (
                  <CustomerRow key={customer.id} customer={customer} saving={saving}
                    onEdit={startEdit} onArchive={() => archive(customer, false)} />
                ))}
              </RecordGroup>
              {archivedCustomers.length > 0 && (
                <RecordGroup title="Archived" count={archivedCustomers.length} emptyLabel="No archived customers.">
                  {archivedCustomers.map((customer) => (
                    <CustomerRow key={customer.id} customer={customer} saving={saving} archived
                      onEdit={startEdit} onArchive={() => archive(customer, true)} />
                  ))}
                </RecordGroup>
              )}
            </>
          )}
        </div>

        <FormPanel
          title={editing ? 'Edit customer' : 'Add customer'}
          description="These records feed the customer picker used when creating or editing a job."
          className="xl:sticky xl:top-2"
        >
          <form onSubmit={save} className="flex flex-col gap-4 p-5">
            <Field label="Name">
              <TextInput value={form.name} required onChange={(event) => setField('name', event.target.value)}
                placeholder="Highfield Motors" />
            </Field>
            <Field label="Nickname" optional>
              <TextInput value={form.nickname} onChange={(event) => setField('nickname', event.target.value)}
                placeholder="What the yard calls them" />
            </Field>
            <Field label="Company name" optional>
              <TextInput value={form.companyName} onChange={(event) => setField('companyName', event.target.value)} />
            </Field>
            <Field label="Address" optional hint="Used as the default drop-off when this customer is chosen.">
              <TextArea value={form.companyAddress} rows={3}
                onChange={(event) => setField('companyAddress', event.target.value)} />
            </Field>
            <Field label="Phone" optional>
              <TextInput value={form.companyPhone} type="tel" onChange={(event) => setField('companyPhone', event.target.value)} />
            </Field>
            <Field label="Status" optional>
              <TextInput value={form.companyStatus} onChange={(event) => setField('companyStatus', event.target.value)} />
            </Field>
            <Field label="Notes" optional>
              <TextArea value={form.notes} rows={3} onChange={(event) => setField('notes', event.target.value)} />
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-[10px] bg-surface px-3.5 py-3">
              <span>
                <span className="block text-[15px] font-semibold text-ink">Offer in job pickers</span>
                <span className="mt-0.5 block text-[13px] text-ink3">Turn off to archive without losing the record.</span>
              </span>
              <Switch label="Offer in job pickers" checked={form.isActive}
                onChange={(checked) => setField('isActive', checked)} />
            </div>

            <div className="flex gap-2">
              <Button type="submit" loading={saving} disabled={saving || !form.name.trim()}>
                {editing ? 'Save changes' : 'Add customer'}
              </Button>
              {editing && <Button type="button" variant="secondary" onClick={startNew}>Cancel</Button>}
            </div>
          </form>
        </FormPanel>
      </div>
    </div>
  );
}

function CustomerRow({
  customer, archived = false, saving, onEdit, onArchive,
}: {
  customer: Customer;
  archived?: boolean;
  saving: boolean;
  onEdit: (customer: Customer) => void;
  onArchive: () => void;
}) {
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 rounded-[14px] border border-hair bg-elev px-4 py-3.5 ${archived ? 'opacity-60' : ''}`}>
      <div className="min-w-0">
        <button type="button" onClick={() => onEdit(customer)}
          className="text-left text-[16px] font-semibold text-ink hover:text-accent">
          {customerTitle(customer)}
        </button>
        {customer.companyAddress && (
          <p className="mt-0.5 line-clamp-2 text-[14px] text-ink2">{customer.companyAddress}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-ink3">
          {customer.companyPhone && <span className="font-mono">{customer.companyPhone}</span>}
          {customer.companyStatus && <span>{customer.companyStatus}</span>}
          {customer.updatedAt && <span>Updated <span className="font-mono">{formatTimestamp(customer.updatedAt)}</span></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={() => onEdit(customer)}>Edit</Button>
        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={onArchive}>
          {archived ? 'Reactivate' : 'Archive'}
        </Button>
      </div>
    </div>
  );
}

function customerTitle(customer: Customer) {
  const name = customer.name || 'Unnamed customer';
  const nickname = customer.nickname?.trim();
  return nickname && nickname.toLowerCase() !== name.toLowerCase()
    ? `${nickname} - ${name}`
    : name;
}
